import React from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { RevenueSplit } from '../../services/revenue-split-service';

interface SplitListModalProps {
  visible: boolean;
  onClose: () => void;
  splits: RevenueSplit[];
  chargerName?: string;
}

/**
 * Modal that displays the list of active splits when badge is pressed
 */
const SplitListModal: React.FC<SplitListModalProps> = ({
  visible,
  onClose,
  splits,
  chargerName,
}) => {
  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <TouchableOpacity
        style={styles.overlay}
        activeOpacity={1}
        onPress={onClose}
      >
        <TouchableOpacity
          style={styles.modalContainer}
          activeOpacity={1}
          onPress={(e) => e.stopPropagation()}
        >
          <View style={styles.header}>
            <View style={styles.headerLeft}>
              <Ionicons name="people" size={20} color="#3B82F6" />
              <Text style={styles.headerTitle}>Revenue Splits</Text>
            </View>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Ionicons name="close" size={24} color="#8D8E90" />
            </TouchableOpacity>
          </View>

          {chargerName && (
            <Text style={styles.chargerName}>{chargerName}</Text>
          )}

          <ScrollView style={styles.splitList}>
            {splits.length === 0 ? (
              <Text style={styles.emptyText}>No active splits</Text>
            ) : (
              splits.map((split) => (
                <View key={split.splitId} style={styles.splitItem}>
                  <View style={styles.splitInfo}>
                    <Text style={styles.partnerName}>
                      {split.partner?.username || split.partnerUsername || 'Partner'}
                    </Text>
                    {split.partner?.email && (
                      <Text style={styles.partnerEmail}>{split.partner.email}</Text>
                    )}
                  </View>
                  <Text style={styles.percentage}>{split.splitPercentage}%</Text>
                </View>
              ))
            )}
          </ScrollView>

          {splits.length > 0 && (
            <View style={styles.footer}>
              <Text style={styles.footerLabel}>Total Split:</Text>
              <Text style={styles.footerValue}>
                {splits.reduce((sum, s) => sum + s.splitPercentage, 0).toFixed(2)}%
              </Text>
            </View>
          )}
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContainer: {
    backgroundColor: '#1A1D23',
    borderRadius: 16,
    width: '100%',
    maxWidth: 400,
    maxHeight: '80%',
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#292D33',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  headerTitle: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '700',
  },
  closeButton: {
    padding: 4,
  },
  chargerName: {
    color: '#8D8E90',
    fontSize: 14,
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 8,
  },
  splitList: {
    padding: 16,
  },
  splitItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#292D33',
    padding: 12,
    borderRadius: 12,
    marginBottom: 8,
  },
  splitInfo: {
    flex: 1,
  },
  partnerName: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 2,
  },
  partnerEmail: {
    color: '#8D8E90',
    fontSize: 12,
  },
  percentage: {
    color: '#3B82F6',
    fontSize: 18,
    fontWeight: '700',
  },
  emptyText: {
    color: '#8D8E90',
    fontSize: 14,
    textAlign: 'center',
    paddingVertical: 20,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: '#292D33',
    backgroundColor: '#0B1E1D',
  },
  footerLabel: {
    color: '#8D8E90',
    fontSize: 14,
    fontWeight: '600',
  },
  footerValue: {
    color: '#3B82F6',
    fontSize: 18,
    fontWeight: '700',
  },
});

export default SplitListModal;

